import { supabase } from '@/integrations/supabase/client';
import { openAIService } from './openaiService';

export interface UserProfile {
  name: string;
  id: string;
  // Supabase 'profiles' columns
  user_id?: string;
  sprint_start_date?: string | null;
  onboarding_completed?: boolean | null;
  development_stage?: string | null;
  idea_description?: string | null;
  monthly_tool_spend?: number | null;
  pod_id?: string | null;
  created_at?: string;
  updated_at?: string;
  // App-specific derived/legacy fields (optional)
  onboarding_date?: string;
  idea_validated?: boolean;
  tutorial_completed?: boolean;
  subscription_status?: 'free' | 'pro' | 'yearly';
  current_sprint_day?: number;
  streak_days?: number;
  phone?: string;
  company?: string;
  role?: string;
  bio?: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
}

export interface Task {
  id: string;
  user_id: string;
  title: string;
  description?: string | null;
  completed: boolean | null;
  due_date?: string | null;
  // Not in DB, kept optional for UI
  priority?: 'high' | 'medium' | 'low';
  category?: string;
  created_at: string;
}

export interface JournalEntry {
  id: string;
  user_id: string;
  title: string;
  content: string;
  mood?: string;
  tags?: string[];
  ai_analysis?: string;
  created_at: string;
}

export interface ROIEntry {
  id: string;
  user_id: string;
  type: string; // Supabase column is string
  amount: number;
  category?: string | null;
  description: string;
  date: string;
  created_at: string;
}

export interface PodAssignment {
  id: string;
  user_id: string;
  pod_id: string;
  joined_at: string;
}

export interface Pod {
  id: string;
  name: string;
  description?: string;
  member_count: number;
  created_at: string;
  role: string;
}

export interface ChatMessage {
  id: string;
  pod_id: string;
  user_id: string;
  message: string;
  created_at: string;
}

export interface Achievement {
  id: string;
  user_id: string;
  badge_name: string;
  badge_type: 'milestone' | 'streak' | 'activity' | 'special' | string;
  description: string;
  icon?: string;
  earned_at: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface NotificationItem {
  id: string;
  user_id: string;
  content: string;
  type: string;
  read_status: boolean;
  context?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

class DataService {
  // User Profile Methods
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      return data ? { ...data, name: data.full_name || 'User' } as UserProfile : null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  // Notifications Methods
  getUserNotifications = async (userId: string): Promise<NotificationItem[]> => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as NotificationItem[]) || [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  markNotificationRead = async (notificationId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_status: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  createNotification = async (userId: string, content: string, type: string = 'info', context?: Record<string, any>): Promise<NotificationItem | null> => {
    try {
      const insertable: any = {
        user_id: userId,
        content,
        type,
        context: context ?? null,
        read_status: false,
      };
      const { data, error } = await supabase
        .from('notifications')
        .insert([insertable])
        .select('*')
        .single();

      if (error) throw error;
      return data as NotificationItem;
    } catch (error) {
      console.error('Error creating notification:', error);
      return null;
    }
  }

  async createUserProfile(userId: string, defaults: Partial<UserProfile> = {}): Promise<UserProfile | null> {
    try {
      const base: any = {
        user_id: userId,
        onboarding_completed: false,
        tutorial_completed: false,
        sprint_start_date: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...defaults,
      };
      const { data, error } = await supabase
        .from('profiles')
        .insert([base])
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as UserProfile;
    } catch (error) {
      console.error('Error creating user profile:', error);
      return null;
    }
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates as any)
        .eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      return false;
    }
  }

  // Task Methods
  async getUserTasks(userId: string): Promise<Task[]> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(task => ({ ...task, priority: task.priority as 'high' | 'medium' | 'low' || 'medium' }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    }
  }

  async createTask(task: Omit<Task, 'id' | 'created_at'>): Promise<Task | null> {
    try {
      // Only insert columns that exist in DB
      const insertable = {
        user_id: task.user_id,
        title: task.title,
        description: task.description ?? null,
        due_date: task.due_date ?? null,
        completed: task.completed ?? false,
        sprint_day: (task as any).sprint_day ?? null,
      };
      const { data, error } = await supabase
        .from('tasks')
        .insert([insertable])
        .select()
        .single();

      if (error) throw error;
      return { ...data, priority: data.priority as 'high' | 'medium' | 'low' || 'medium' };
    } catch (error) {
      console.error('Error creating task:', error);
      return null;
    }
  }

  async updateTask(taskId: string, updates: Partial<Task>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', taskId);

      if (error) throw error;
      
      // The database trigger will automatically update streak when task completion changes
      return true;
    } catch (error) {
      console.error('Error updating task:', error);
      return false;
    }
  }

  async deleteTask(taskId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting task:', error);
      return false;
    }
  }

  // Journal Methods
  async getUserJournalEntries(userId: string): Promise<JournalEntry[]> {
    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      return [];
    }
  }

  async createJournalEntry(entry: Omit<JournalEntry, 'id' | 'created_at'>): Promise<JournalEntry | null> {
    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .insert([entry])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating journal entry:', error);
      return null;
    }
  }

  // ROI Methods
  async getUserROIEntries(userId: string): Promise<ROIEntry[]> {
    try {
      const { data, error } = await supabase
        .from('roi_entries')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching ROI entries:', error);
      return [];
    }
  }

  async createROIEntry(entry: Omit<ROIEntry, 'id' | 'created_at'>): Promise<ROIEntry | null> {
    try {
      const { data, error } = await supabase
        .from('roi_entries')
        .insert([entry])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating ROI entry:', error);
      return null;
    }
  }

  // Pod Methods
  async getUserPods(userId: string): Promise<Pod[]> {
    try {
      // Step 1: fetch the user's pod assignments
      const { data: assignments, error: assignErr } = await supabase
        .from('pods_assignments')
        .select('pod_id')
        .eq('user_id', userId);

      if (assignErr) throw assignErr;

      const podIds = (assignments || []).map((a: { pod_id: string }) => a.pod_id);
      if (podIds.length === 0) return [];

      // Step 2: fetch pods by ids
      const { data: pods, error: podsErr } = await supabase
        .from('pods')
        .select('*')
        .in('id', podIds);

      if (podsErr) throw podsErr;
      return (pods || []).map(pod => ({ ...pod, role: 'member' }));
    } catch (error) {
      console.error('Error fetching user pods:', error);
      return [];
    }
  }

  async assignUserToPod(userId: string, podId: string): Promise<boolean> {
    try {
      // First, check if the user is already assigned to this pod
      const { data: existingAssignment, error: checkError } = await supabase
        .from('pods_assignments')
        .select('id')
        .eq('user_id', userId)
        .eq('pod_id', podId)
        .maybeSingle();

      // If user is already assigned to this pod, return true without making changes
      if (existingAssignment) {
        return true;
      }

      // If there was an error checking the assignment, throw it
      if (checkError) throw checkError;

      // User is not assigned to this pod, proceed with assignment
      const { error: insertError } = await supabase
        .from('pods_assignments')
        .insert([{ user_id: userId, pod_id: podId }]);

      if (insertError) throw insertError;

      // Increment pod member count safely
      const { data: pod, error: podError } = await supabase
        .from('pods')
        .select('member_count')
        .eq('id', podId)
        .single();

      if (podError) throw podError;

      const current = (pod?.member_count ?? 0);
      
      const { error: updateError } = await supabase
        .from('pods')
        .update({ member_count: current + 1 })
        .eq('id', podId);

      if (updateError) throw updateError;

      return true;
    } catch (error) {
      console.error('Error in assignUserToPod:', error);
      return false;
    }
  }

  async getPodMessages(podId: string, limit: number = 50): Promise<ChatMessage[]> {
    try {
      const { data, error } = await supabase
        .from('pod_messages')
        .select('*')
        .eq('pod_id', podId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching pod messages:', error);
      return [];
    }
  }

  async createPodMessage(message: Omit<ChatMessage, 'id' | 'created_at'>): Promise<ChatMessage | null> {
    try {
      const insertable = {
        pod_id: message.pod_id,
        user_id: message.user_id,
        message: message.message,
      };
      const { data, error } = await supabase
        .from('pod_messages')
        .insert([insertable])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating pod message:', error);
      return null;
    }
  }

  // AI: Generate a Cashpurr message for a pod and persist it
  async generatePodAIMessage(podId: string, seedPrompt?: string): Promise<string | null> {
    try {
      const system: string = 'You are Cashpurr, a witty but practical investor-cat helping a founders\' pod. Be concise (1-3 sentences), specific, actionable. Avoid generic platitudes.';
      const userPrompt = seedPrompt && seedPrompt.trim().length > 0
        ? `A member asked: "${seedPrompt}". Reply with a focused, tactical answer.`
        : 'Post a helpful check-in question to spark discussion about MVP validation, pricing, or customer research.';

      const content = await openAIService.createChatCompletion([
        { role: 'system', content: system },
        { role: 'user', content: userPrompt }
      ], {
        temperature: 0.7,
        max_tokens: 150
      });

      const message = content?.trim() || 'Let\'s talk MVPs: what\'s the smallest test you can run this week?';

      await supabase
        .from('pod_messages')
        .insert({ pod_id: podId, user_id: 'cashpurr', message });

      return message;
    } catch (e) {
      console.error('Error generating pod AI message:', e);
      return null;
    }
  }

  async replyToUserQuestion(podId: string, question: string): Promise<string | null> {
    return this.generatePodAIMessage(podId, question);
  }

  // Achievements Methods
  async getUserAchievements(userId: string): Promise<Achievement[]> {
    try {
      // Not in generated types yet; bypass TS with any
      const { data, error } = await (supabase as any)
        .from('achievements')
        .select('*')
        .eq('user_id', userId)
        .order('earned_at', { ascending: false });

      if (error) throw error;
      return (data as Achievement[]) || [];
    } catch (error) {
      console.error('Error fetching achievements:', error);
      return [];
    }
  }

  async checkAndAwardStreakBadges(userId: string): Promise<void> {
    try {
      // Call the Supabase function defined in migrations (not in types yet)
      const { error } = await (supabase as any).rpc('check_and_award_streak_badges', { p_user_id: userId });
      if (error) throw error;
    } catch (error) {
      console.error('Error awarding streak badges:', error);
    }
  }

  // Utility Methods
  calculateSprintDay(onboardingDate: string): number {
    const startDate = new Date(onboardingDate);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.min(diffDays, 30);
  }

  // Update user streak in database (now based on days since idea validation)
  async updateUserStreak(userId: string): Promise<void> {
    try {
      await supabase.rpc('update_user_streak', { p_user_id: userId });
    } catch (error) {
      console.error('Error updating user streak:', error);
    }
  }

  // Get latest idea assessment date for streak calculation
  async getLatestIdeaAssessmentAt(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.rpc('get_latest_idea_assessment_date', {
        p_user_id: userId
      });

      if (error) throw error;
      return data ? data.toString() : null;
    } catch (error) {
      console.error('Error fetching latest idea assessment date:', error);
      return null;
    }
  }

  // Calculate days remaining in 30-day sprint
  calculateDaysRemaining(streakDays: number): number {
    return Math.max(0, 30 - streakDays);
  }

  calculateDaysWorked(onboardingDate?: string | null): number {
    if (!onboardingDate) return 0;
    const start = new Date(onboardingDate);
    const today = new Date();
    // Use date difference in whole days
    const diffMs = today.setHours(0,0,0,0) - start.setHours(0,0,0,0);
    const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    return days;
  }

  // Initialize default data for new users  
  async initializeUserData(userId: string): Promise<void> {
    try {
      // Get user's profile to determine idea description for personalized tasks
      const profile = await this.getUserProfile(userId);
      const ideaDescription = profile?.idea_description;

      // Generate personalized 30-day task schedule
      await this.generate30DayTasks(userId, ideaDescription);

      // Auto-assign to Product Builders pod
      const productBuildersPod = await this.getOrCreateProductBuildersPod();
      if (productBuildersPod) {
        await this.assignUserToPod(userId, productBuildersPod.id);
        // Generate welcome AI message
        await this.generateWelcomeMessage(productBuildersPod.id, userId);
      }

    } catch (error) {
      console.error('Error initializing user data:', error);
    }
  }

  // Generate personalized 30-day task schedule
  async generate30DayTasks(userId: string, ideaDescription?: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('generate_30_day_tasks', {
        p_user_id: userId,
        p_idea_description: ideaDescription || null
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error generating 30-day tasks:', error);
    }
  }

  // Generate AI welcome message for new pod member
  private async generateWelcomeMessage(podId: string, newUserId: string): Promise<void> {
    try {
      // Check if pod has any messages first
      const { data: existingMessages } = await supabase
        .from('pod_messages')
        .select('id')
        .eq('pod_id', podId)
        .limit(1);

      // Only create welcome messages if pod is empty
      if (!existingMessages || existingMessages.length === 0) {
        // Insert initial AI message (using null user_id for AI)
        await supabase
          .from('pod_messages')
          .insert({
            pod_id: podId,
            user_id: null, // AI message
            message: '🐱 Welcome to Product Builders! Share your current project and let\'s help each other succeed!'
          });

        // Add a follow-up engagement message  
        setTimeout(async () => {
          const engagementMessages = [
            "💡 Pro tip: Share your daily wins here, no matter how small! The community loves celebrating progress together.",
            "🔥 This week's challenge: Get one piece of user feedback on your product. Who's in?",
            "📈 What metrics are you tracking? Even simple ones like website visits count as progress!",
            "🤝 Don't forget to support your fellow founders! A simple 'keep going' can make someone's day.",
            "⚡ What's the biggest obstacle you're facing this week? Let's problem-solve together!"
          ];

          const randomEngagement = engagementMessages[Math.floor(Math.random() * engagementMessages.length)];
          
          await supabase
            .from('pod_messages')
            .insert({
              pod_id: podId,
              user_id: null, // AI message
              message: `🐱 ${randomEngagement}`,
            });
        }, 3000); // 3 second delay for natural conversation flow
      }

    } catch (error) {
      console.error('Error generating welcome message:', error);
    }
  }

  // Generate daily AI engagement messages for pods
  async generateDailyEngagement(): Promise<void> {
    try {
      const { data: pods } = await supabase
        .from('pods')
        .select('id, name');

      // Use a system user id for authored messages; if not present, skip
      const systemUserId = (import.meta as any).env?.VITE_SYSTEM_USER_ID as string | undefined;
      if (!systemUserId) return;

      if (pods) {
        for (const pod of pods) {
          const dailyMessages = [
            "🌅 Good morning, builders! What's the one thing you're most excited to work on today?",
            "⚡ Midday energy check! How's everyone's progress looking? Share a quick win!",
            "🎯 Focus time! What's your #1 priority for the rest of the day?",
            "💪 Afternoon motivation: Remember, every successful founder started exactly where you are now!",
            "🔥 End of day reflection: What did you learn about your customers today?",
            "📊 Weekly check-in: How are your key metrics looking? Any surprises?",
            "🚀 Weekend warrior mode: Who's putting in some extra hustle this weekend?",
            "💡 Idea sharing time: What's one feature your users are asking for?",
            "🎉 Celebration station: Drop your wins below - big or small, we want to hear them!",
            "🤔 Problem-solving session: What challenge can the community help you with?"
          ];

          // Only send if no recent messages in the last 6 hours (avoid spam)
          const { data: recentMessages } = await supabase
            .from('pod_messages')
            .select('created_at')
            .eq('pod_id', pod.id)
            .gte('created_at', new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString())
            .limit(1);

          if (!recentMessages || recentMessages.length === 0) {
            const randomMessage = dailyMessages[Math.floor(Math.random() * dailyMessages.length)];

            await supabase
              .from('pod_messages')
              .insert({
                pod_id: pod.id,
                user_id: systemUserId,
                message: randomMessage,
              });
          }
        }
      }
    } catch (error) {
      console.error('Error generating daily engagement:', error);
    }
  }

  private async getOrCreateProductBuildersPod(): Promise<Pod | null> {
    try {
      // Check if Product Builders pod exists
      const { data: existingPod, error: fetchError } = await supabase
        .from('pods')
        .select('*')
        .eq('name', 'Product Builders')
        .single();

      if (existingPod) {
        // Ensure existing pod has minimum member count of 104
        if (existingPod.member_count < 104) {
          await supabase
            .from('pods')
            .update({ member_count: 104 })
            .eq('id', existingPod.id);
          existingPod.member_count = 104;
        }
        return { ...existingPod, role: 'member' };
      }

      // Create Product Builders pod if it doesn't exist
      const { data: newPod, error: createError } = await supabase
        .from('pods')
        .insert([{ 
          name: 'Product Builders',
          description: 'Connect with fellow founders building products',
          member_count: 104
        }])
        .select()
        .single();

      if (createError) throw createError;
      return { ...newPod, role: 'member' };
    } catch (error) {
      console.error('Error getting/creating Product Builders pod:', error);
      return null;
    }
  }
  async hasPodAssignment(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('pods_assignments')
        .select('pod_id')
        .eq('user_id', userId)
        .limit(1);
      if (error) throw error;
      return !!(data && data.length > 0);
    } catch (e) {
      console.error('Error checking pod assignment:', e);
      return false;
    }
  }

  async ensureAssignedToProductBuilders(userId: string): Promise<Pod | null> {
    try {
      // If already assigned, return the existing pod (first one)
      const assigned = await this.hasPodAssignment(userId);
      if (assigned) {
        const pods = await this.getUserPods(userId);
        return pods[0] ?? null;
      }

      // Ensure default pod exists
      const pod = await this.getOrCreateProductBuildersPod();
      if (!pod) return null;

      // Assign and bump member count
      const ok = await this.assignUserToPod(userId, pod.id);
      if (!ok) return null;

      // Drop a lightweight welcome line to seed conversation
      await this.generateWelcomeMessage(pod.id, userId);
      return pod;
    } catch (e) {
      console.error('Error ensuring Product Builders assignment:', e);
      return null;
    }
  }
}

export const dataService = new DataService();
